import { useState, useEffect, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { browserDeeplink } from '../../../lib/browser-deeplink';
import EnterLayout from '../../components/layout/EnterLayout';
import Alert from '../../components/common/Alert';
import { getDownloadLink } from '../../components/common/FormatMessageComponents';
import { resolvePlatform, isMobile } from '../../utils/platformResolver';
import { defaultLinks, fetchPersonalizedLinks } from '../../utils/downloadLinks';
import { logDownloadStarted } from '../../utils/analytics/downloadEvents';
import { sanitizeUrl } from '../../utils/sanitizeUrl';

/**
 * Takes `app_url` param as base URL, and appends all the remaining params to it
 */
const createAppLink = (): string => {
  const params = new URLSearchParams(window.location.search);

  const appUrlParam = params.get('app_url');

  let appUrl = 'clearvpn://';

  if (
    appUrlParam?.startsWith('clearvpn://') ||
    appUrlParam?.startsWith('clearvpnsite://') ||
    appUrlParam?.startsWith('clearvpnmas://')
  ) {
    appUrl = sanitizeUrl(appUrlParam);
  }

  params.delete('app_url');

  const url = new URL(appUrl);

  params.forEach((value, key) => {
    url.searchParams.set(key, value);
  });

  return url.toString();
};

interface RedirectToAppProps {
  title: string;
}

/**
 * Static alert with "To App" button. The link is constructed from query params.
 * @returns {JSX.Element}
 */
const RedirectToApp = ({ title }: RedirectToAppProps) => {
  // this must be correct on FIRST RENDER, or `autoClickAction` will brake app login
  const appLink = useRef(createAppLink()).current;
  const isMobileLink = useRef(isMobile()).current;

  const [downloadLink, setDownloadLink] = useState<string | undefined>();

  useEffect(() => {
    const platform = resolvePlatform();

    if (platform) {
      setDownloadLink(defaultLinks[platform]);

      const params = new URLSearchParams(window.location.search).toString();

      void fetchPersonalizedLinks(params).then((downloadLinks) => {
        setDownloadLink(downloadLinks[platform]);
      });
    }
  }, []);

  const [hasFailedToOpenApp, setHasFailedToOpenApp] = useState(false);
  const onClick = () => {
    browserDeeplink(appLink).catch((error: unknown) => {
      console.error('Deeplink did’t open', error);
      setHasFailedToOpenApp(true);
    });
  };

  const intl = useIntl();

  return (
    <EnterLayout verticalPaddings={{ top: 90, bottom: 60 }}>
      <Alert
        autoClickAction
        href={isMobileLink ? appLink : undefined}
        title={title}
        icon="success"
        actionTitle={intl.formatMessage({
          id: 'redirectToApp.cta',
          defaultMessage: 'Go to App',
          description: 'Button that opens up ClearVPN app',
        })}
        footerChild={
          hasFailedToOpenApp && downloadLink ? (
            <FormattedMessage
              id="redirectToApp.downloadApp"
              defaultMessage="It seems, you don’t have the app installed yet, please, <link>download here.</link>"
              description="Banner on `To app` page shown, when user can't open the deeplink, because they does’t have it installed"
              values={{
                link: getDownloadLink({
                  href: downloadLink,
                  isMobile: isMobile(),
                  onClick: () => {
                    logDownloadStarted(resolvePlatform());
                  },
                }),
              }}
            />
          ) : null
        }
        onClick={isMobileLink ? undefined : onClick}
      />
    </EnterLayout>
  );
};

export default RedirectToApp;
